import { jsx, jsxs } from "react/jsx-runtime";
import { Stack, Text, Dialog, Columns, Column, Box } from "braid-design-system";
import * as React from "react";
import { createContext, useState, useContext, useCallback, useEffect } from "react";
import { AdBudgetsUpsell } from "./components/AdBudgetsUpsell/AdBudgetsUpsell.mjs";
import { BalanceCarousel } from "./components/BalanceCarousel/BalanceCarousel.mjs";
import { formatMinorUnits } from "@seek/currency";
import { useTranslations } from "@vocab/react";
import { createUrlResolver } from "@seek/melways-sites";
import { ProgressCircle } from "./components/ProgressCircle/ProgressCircle.mjs";
import { translations } from "../../HirerContractBalance/components/Remaining/.vocab/index.mjs";
import { translations as translations$1 } from "../../HirerContractBalance/components/Contract/.vocab/index.mjs";
import { HirerBalanceLayout } from "./components/HirerBalanceLayout.mjs";
import { root, layout } from "./HirerBalancePresentation.css.mjs";
const HirerBalanceContext = createContext(
  {}
);
const HirerBalanceContextProvider = ({
  children,
  site,
  language,
  isStaging,
  advertiserDetails,
  locale
}) => {
  const [calloutVisible, setCalloutVisible] = useState(false);
  const [contractBalance, setContractBalance] = useState();
  const [warningType, setWarningType] = useState("NoWarning");
  const showCallout = (contractBalanceToShow, warningTypeForContract) => {
    setCalloutVisible(true);
    setContractBalance(contractBalanceToShow);
    setWarningType(warningTypeForContract);
  };
  const hideCallout = () => {
    setCalloutVisible(false);
  };
  const urlResolver = createUrlResolver({
    site,
    language,
    staging: isStaging
  });
  const usersContext = {
    calloutVisible,
    contractBalance,
    warningType,
    advertiserDetails,
    showCallout,
    hideCallout,
    urlResolver,
    locale
  };
  return /* @__PURE__ */ jsx(HirerBalanceContext.Provider, { value: usersContext, children });
};
const compare = (first, second) => {
  if (first < second) {
    return -1;
  } else if (first > second) {
    return 1;
  }
  return 0;
};
const compareContracts = (first, second) => compare(first.expiryDate, second.expiryDate) || compare(first.validFromDate, second.validFromDate) || compare(first.createdDate, second.createdDate);
const centsToDollars = (cents) => Math.floor(cents / 100);
const mapConsumableContract = ({
  contractBalance,
  currentContract
}) => {
  var _a, _b;
  return {
    adType: contractBalance.adType,
    contractType: contractBalance.contractType,
    expiryDate: currentContract.expiry.expiryDate,
    validFromDate: currentContract.validFromDate,
    createdDate: currentContract.createdDate,
    isDollarBased: contractBalance.dollarBased,
    originalValue: contractBalance.dollarBased ? currentContract.value.dollars.original.value || 0 : ((_a = currentContract.value.units) == null ? void 0 : _a.original) || 0,
    remainingValue: contractBalance.dollarBased ? currentContract.value.dollars.remaining.value || 0 : ((_b = currentContract.value.units) == null ? void 0 : _b.remaining) || 0
  };
};
const mapAdType = (adType) => {
  switch (adType) {
    case "CLASSIC":
      return "classic";
    case "BRANDED":
      return "standout";
    case "BRANDED_UPGRADE":
      return "standout-upgrade";
    case "PREMIUM":
      return "premium";
    default:
      return "";
  }
};
const mapAdTypeForSeekContracts = (adType) => {
  switch (adType) {
    case "CLASSIC":
      return "classic";
    case "BRANDED":
      return "branded";
    default:
      return "";
  }
};
const createConsumableContractsTrackingData = (consumableContracts) => {
  const contractTypes = [];
  const isDollarBased = [];
  const originalValue = [];
  const remainingValue = [];
  const adTypes = [];
  consumableContracts.forEach((contract) => {
    contractTypes.push(
      contract.contractType === "SEEK_CONTRACT" ? "seek-contract" : "ad-pack"
    );
    isDollarBased.push(contract.isDollarBased);
    originalValue.push(centsToDollars(contract.originalValue));
    remainingValue.push(centsToDollars(contract.remainingValue));
    adTypes.push(
      contract.contractType === "SEEK_CONTRACT" ? mapAdTypeForSeekContracts(contract.adType) : mapAdType(contract.adType)
    );
  });
  return {
    hirerContractType: contractTypes,
    hirerContractIsDollarBased: isDollarBased,
    hirerContractValue: originalValue,
    hirerContractBalance: remainingValue,
    hirerContractAdType: adTypes
  };
};
const mapAdvertiserTypeHirer = (contractBalance) => {
  const isVariablePriceContract = (balance) => balance.contractType === "SEEK_CONTRACT";
  const isFixedPriceContract = (balance) => balance.contractType === "AD_PACK" || balance.contractType === "MASTER_AGREEMENT" || balance.contractType === "SUBSCRIPTION";
  if (contractBalance.some(isVariablePriceContract)) {
    return "contract";
  } else if (contractBalance.some(isFixedPriceContract)) {
    return "ad-pack";
  }
  return "casual";
};
const getConsumableContracts = (contractBalances) => {
  const potentialConsumableContracts = contractBalances.reduce((aggregator, balanceItem) => {
    var _a;
    const currentContract = (_a = balanceItem.contracts) == null ? void 0 : _a.find(
      (contract) => contract.current
    );
    return currentContract ? [
      mapConsumableContract({
        contractBalance: balanceItem,
        currentContract
      }),
      ...aggregator
    ] : aggregator;
  }, []);
  const sortedContracts = potentialConsumableContracts.sort(compareContracts);
  const consumableContractsMap = /* @__PURE__ */ new Map();
  const adTypesToBeGrouped = ["CLASSIC", "BRANDED"];
  const contractTypesToBeGrouped = ["ADPACK", "SEEK_CONTRACT"];
  sortedContracts.map((contract) => {
    const toBeGrouped = contractTypesToBeGrouped.includes(contract.contractType) && adTypesToBeGrouped.includes(contract.adType);
    const mapKey = toBeGrouped ? contract.contractType : `${contract.contractType}_${contract.adType}`;
    if (!consumableContractsMap.has(mapKey)) {
      consumableContractsMap.set(mapKey, contract);
    }
  });
  return Array.from(consumableContractsMap.values());
};
const advertiserHasStackedContracts = (contractBalances) => {
  const advertiserTotalContracts = contractBalances.reduce(
    (aggregator, balanceItem) => balanceItem.contracts ? aggregator + balanceItem.contracts.length : aggregator,
    0
  );
  return advertiserTotalContracts > 1;
};
const createImpressionTrackingData = (advertiserDetails) => {
  const { contractBalances, pricingModel, hideBalance } = advertiserDetails;
  const consumableContracts = getConsumableContracts(contractBalances);
  const advertiserTypeHirer = mapAdvertiserTypeHirer(contractBalances) || "casual";
  return {
    advertiserBalanceVisibility: hideBalance ? "hide-balance" : "show-balance",
    advertiserTypeHirer,
    advertiserTypePricing: pricingModel === "FIXED" ? "fixed" : "variable",
    advertiserHasStackedContracts: advertiserHasStackedContracts(contractBalances),
    ...consumableContracts.length > 0 && createConsumableContractsTrackingData(consumableContracts)
  };
};
const defaultTrackingCallback = () => {
};
const TrackingContext = React.createContext(
  defaultTrackingCallback
);
const TrackingProvider = ({ children, context }) => /* @__PURE__ */ jsx(TrackingContext.Provider, { value: context, children });
const onImpression = (trackingCallback) => (advertiserDetails) => {
  const impressionTrackingData = createImpressionTrackingData(advertiserDetails);
  return trackingCallback({
    eventName: ["balance_widget_displayed"],
    data: {
      linkName: "balance_widget_displayed",
      ...impressionTrackingData
    }
  });
};
const onBalanceWidgetClicked = (trackingCallback) => (input) => {
  const impressionTrackingData = createImpressionTrackingData(input);
  const contractTrackingData = input.currentConsumableContract && createConsumableContractsTrackingData([input.currentConsumableContract]);
  return trackingCallback({
    eventName: ["balance_widget_pressed"],
    data: {
      ...impressionTrackingData,
      linkName: "balance_widget_pressed",
      actionOrigin: input.actionOrigin,
      objectInteraction: input.objectInteraction,
      ...contractTrackingData
    }
  });
};
const useTracking = () => {
  const trackingContext = useContext(TrackingContext);
  const trackingCallback = trackingContext ?? defaultTrackingCallback;
  return {
    onImpression: useCallback(
      (advertiserDetails) => onImpression(trackingCallback)(advertiserDetails),
      [trackingCallback]
    ),
    onBalanceWidgetClicked: onBalanceWidgetClicked(trackingCallback)
  };
};
const Remaining = (props) => {
  var _a, _b;
  const { t } = useTranslations(translations);
  const { isDollarValue, totalValue, warningType, onMouseOver, onMouseOut } = props;
  const currency = totalValue.dollars.remaining.currency;
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const { locale } = hirerBalanceContext;
  const remainingValue = isDollarValue ? totalValue.dollars.remaining.value ?? 0 : ((_a = totalValue.units) == null ? void 0 : _a.remaining) ?? 0;
  const actualTotalValue = isDollarValue ? totalValue.dollars.original.value ?? 0 : ((_b = totalValue.units) == null ? void 0 : _b.original) ?? 0;
  const remainingPercentage = actualTotalValue > 0 ? remainingValue / actualTotalValue * 100 : 0;
  const isInCredit = remainingValue && remainingValue > 0;
  const minorUnits = formatMinorUnits(Math.floor(remainingValue), {
    currency,
    locale,
    dropFraction: true,
    notation: remainingValue.toString().length < 9 ? "standard" : "compact"
  });
  return isInCredit ? /* @__PURE__ */ jsx(
    ProgressCircle,
    {
      percentage: remainingPercentage,
      warningType,
      onMouseOver,
      onMouseOut,
      children: isDollarValue ? /* @__PURE__ */ jsxs(Stack, { space: "small", align: "center", children: [
        /* @__PURE__ */ jsxs(
          Text,
          {
            weight: "medium",
            size: minorUnits.toString().length > 9 ? "small" : "large",
            children: [
              " ",
              minorUnits
            ]
          }
        ),
        /* @__PURE__ */ jsx(Text, { size: "small", children: t("remaining") })
      ] }) : /* @__PURE__ */ jsxs(Text, { children: [
        remainingValue,
        " "
      ] })
    }
  ) : null;
};
const padToTwoDigits = (input) => `0${input}`.slice(-2);
const formatExpiryDate = (utcDateString) => {
  if (!utcDateString) {
    return "";
  }
  const utcDate = new Date(utcDateString);
  const date = padToTwoDigits(utcDate.getDate());
  const month = padToTwoDigits(utcDate.getMonth() + 1);
  return `${date}/${month}/${utcDate.getFullYear()}`;
};
const ContractBalanceBreakdownDialog = ({
  contractBalance,
  warningType
}) => {
  const { calloutVisible, hideCallout } = useContext(HirerBalanceContext);
  const { t } = useTranslations(translations$1);
  const { contracts, dollarBased, title } = contractBalance;
  const activeContracts = (contracts == null ? void 0 : contracts.filter((x) => x.current)) ?? [];
  const stackedContracts = (contracts == null ? void 0 : contracts.filter((x) => !x.current)) ?? [];
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      id: "cbb-dialog",
      title: t("Balance breakdown"),
      open: calloutVisible,
      onClose: () => hideCallout(),
      width: "small",
      children: [
        activeContracts.map((contract, index) => /* @__PURE__ */ jsx(
          ContractComponent,
          {
            contract,
            warningType,
            isDollarValue: dollarBased,
            label: title
          },
          index
        )),
        stackedContracts.map((contract, index) => /* @__PURE__ */ jsx(
          ContractComponent,
          {
            contract,
            warningType: "NoWarning",
            isDollarValue: dollarBased,
            label: title,
            isStacked: true
          },
          index
        ))
      ]
    }
  );
};
const ContractComponent = ({
  contract,
  warningType,
  isDollarValue,
  label,
  isStacked
}) => {
  var _a;
  const { locale } = useContext(HirerBalanceContext);
  const { t } = useTranslations(translations$1);
  const {
    expiry: { expiryDate },
    value
  } = contract;
  const remainingValue = isDollarValue ? value.dollars.remaining.value ?? 0 : ((_a = value.units) == null ? void 0 : _a.remaining) ?? 0;
  const isCompactPrice = remainingValue.toString().length >= 9;
  const currency = value.dollars.remaining.currency;
  const hasValidExpiryDate = isStacked ? false : !expiryDate.startsWith("9999");
  return /* @__PURE__ */ jsxs(Columns, { space: "large", collapseBelow: "desktop", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
      Remaining,
      {
        totalValue: value,
        isDollarValue,
        warningType
      }
    ) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "center", height: "full", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { size: "large", weight: "strong", children: label }),
      /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
        isCompactPrice && /* @__PURE__ */ jsxs(Text, { children: [
          formatMinorUnits(Math.floor(remainingValue), {
            currency,
            locale,
            dropFraction: true
          }),
          t("remaining")
        ] }),
        /* @__PURE__ */ jsx(Text, { children: hasValidExpiryDate ? t("Expires date", {
          date: formatExpiryDate(expiryDate)
        }) : t("Not started") })
      ] })
    ] }) }) })
  ] });
};
const HirerBalance = ({ contractBalances }) => {
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const {
    calloutVisible,
    contractBalance: calloutContractBalance,
    warningType: calloutWarningType,
    advertiserDetails
  } = hirerBalanceContext;
  const noContracts = contractBalances.length === 0;
  if (noContracts || advertiserDetails.hideBalance) {
    return /* @__PURE__ */ jsx(HirerBalanceLayout, { children: /* @__PURE__ */ jsx(AdBudgetsUpsell, {}) });
  }
  return /* @__PURE__ */ jsx(Box, { className: root, children: /* @__PURE__ */ jsxs(Box, { position: "relative", background: "surface", className: layout, children: [
    calloutVisible && calloutContractBalance && /* @__PURE__ */ jsx(
      ContractBalanceBreakdownDialog,
      {
        contractBalance: calloutContractBalance,
        warningType: calloutWarningType
      }
    ),
    /* @__PURE__ */ jsx(BalanceCarousel, { balances: contractBalances })
  ] }) });
};
const HirerBalancePresentation = (props) => {
  const { contractBalances, pricingModel, site, language, isStaging, locale } = props;
  const hideBalance = contractBalances.length > 0 ? contractBalances.every((balance) => balance.hideBalance) : false;
  const sortedContractBalances = contractBalances.filter((c) => c.dollarBased).concat(contractBalances.filter((c) => !c.dollarBased));
  const advertiserDetails = {
    contractBalances: sortedContractBalances,
    pricingModel,
    hideBalance
  };
  const { onImpression: onImpression2 } = useTracking();
  useEffect(() => {
    onImpression2({ contractBalances, hideBalance, pricingModel });
  }, [contractBalances, pricingModel, hideBalance, onImpression2]);
  return /* @__PURE__ */ jsx(
    HirerBalanceContextProvider,
    {
      advertiserDetails,
      site,
      language,
      isStaging,
      locale,
      children: /* @__PURE__ */ jsx(HirerBalance, { contractBalances: sortedContractBalances })
    }
  );
};
export {
  HirerBalanceContext,
  HirerBalancePresentation,
  Remaining,
  TrackingProvider,
  formatExpiryDate,
  mapConsumableContract,
  useTracking
};
